import { ReactNode, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Jodit } from 'jodit-react';

import { InputLabel } from '@/components/InputLabel';

import { cn } from '@/lib/utils';

import { useTheme } from '@/providers';

import { JoditEditorWrapper } from './JoditEditorWrapper';

type JoditConfig = Record<string, unknown>;

const HIDDEN_LINK_POPUP_BUTTONS = ['brush', 'file'];

const DEFAULT_LINK_POPUP = Jodit.defaultOptions.popup.a;

const LINK_POPUP_BUTTONS = Array.isArray(DEFAULT_LINK_POPUP)
  ? Jodit.atom(
      DEFAULT_LINK_POPUP.filter(
        (button) =>
          typeof button !== 'string' ||
          !HIDDEN_LINK_POPUP_BUTTONS.includes(button),
      ),
    )
  : DEFAULT_LINK_POPUP;

type TextEditorProps = {
  id?: string;
  value?: string;
  label?: ReactNode;
  hint?: ReactNode;
  'aria-label'?: string;
  isRequired?: boolean;
  disabled?: boolean;
  'aria-invalid'?: boolean;
  onChange?: (value: string) => void;
  onBlur?: (value: string) => void;
  className?: string;
};

const TextEditor = (props: TextEditorProps) => {
  const {
    id,
    value,
    label,
    hint,
    'aria-label': ariaLabel,
    isRequired,
    disabled,
    'aria-invalid': ariaInvalid,
    onChange,
    onBlur,
    className,
  } = props;

  const { i18n, t } = useTranslation();
  const { theme } = useTheme();
  const generatedId = useId();
  const editorId = id ?? generatedId;

  const [isFocused, setIsFocused] = useState(false);

  // jodit-react re-runs its value-sync useEffect whenever its `value` prop
  // changes and rewrites the editor's innerHTML if `jodit.value !== value`
  // (see node_modules/jodit-react/src/JoditEditor.tsx). Because Jodit
  // normalises HTML internally, even the string Jodit itself emits can fail
  // that equality check on write-back, which resets the caret to position 0.
  //
  // To avoid this, we keep the `value` prop we pass to Jodit referentially
  // stable across typing renders: we only update it when a value arrives
  // that was NOT just emitted by Jodit (i.e. a genuine external change like
  // form reset, setValue, or edit-mode load). `lastEmittedValue` tracks what
  // Jodit last emitted so we can ignore the react-hook-form echo.
  const [editorValue, setEditorValue] = useState<string>(value || '');
  const [lastEmittedValue, setLastEmittedValue] = useState<string>(value || '');

  // React's recommended pattern for syncing derived state from props without
  // useEffect — condition-guarded setState during render.
  const incomingValue = value || '';
  if (incomingValue !== editorValue && incomingValue !== lastEmittedValue) {
    setEditorValue(incomingValue);
    setLastEmittedValue(incomingValue);
  }

  const getJoditLanguage = (lang: string): string => {
    const languageMap: Record<string, string> = {
      en: 'en',
      uk: 'uk',
    };
    return languageMap[lang] || 'en';
  };

  const getJoditTranslations = useMemo(() => {
    const translations: Record<string, string> = {};

    const joditKeys = [
      'Bold',
      'Italic',
      'Underline',
      'Strike through',
      'Clear formatting',
      'Insert link',
      'Unlink',
      'Insert Unordered List',
      'Default',
      'Insert Ordered List',
      'Insert format block',
      'Paragraph',
      'Heading 1',
      'Heading 2',
      'Heading 3',
      'Heading 4',
      'Quote',
      'Code',
      'Undo',
      'Redo',
      'Type something',
      'Insert image',
      'Insert table',
      'Source code',
      'Fullsize',
      'Shrink',
      'Select all',
      'Clear Formatting',
      'URL',
      'Text',
      'Open in new tab',
      'Insert',
      'Update',
      'Paste as HTML',
      'Your code is similar to HTML. Keep as HTML?',
      'Keep',
      'Word Paste Detected',
      'The pasted content is coming from a Microsoft Word/Excel document. Do you want to keep the format or clean it up?',
      'Clean',
      'Cancel',
    ];

    joditKeys.forEach((key) => {
      const translationKey = `jodit.${key}`;
      const translated = t(translationKey);

      if (translated && translated !== translationKey) {
        translations[key] = translated;
      }
    });

    return translations;
  }, [t]);

  const config = useMemo((): JoditConfig => {
    const joditLanguage = getJoditLanguage(i18n.language);
    const baseConfig: JoditConfig = {
      readonly: disabled || false,
      placeholder: '',
      language: joditLanguage,
      theme: theme === 'dark' ? 'dark' : 'default',
      toolbarAdaptive: false,
      statusbar: false,
      pasteHTMLActionList: [{ value: 'insert_as_html', text: 'Keep' }],
      pasteFromWordActionList: [{ value: 'insert_as_text', text: 'Clean' }],
      popup: { a: LINK_POPUP_BUTTONS },
      link: {
        modeClassName: false,
        noFollowCheckbox: false,
      },
      buttons: [
        'bold',
        'italic',
        'underline',
        'strikethrough',
        'eraser',
        '|',
        'ul',
        'ol',
        '|',
        'link',
        '|',
        'paragraph',
        '|',
        'undo',
        'redo',
      ],
      controls: {
        ul: {
          list: ['default'],
        },
        ol: {
          list: ['default'],
        },
      },
      events: {
        focus: () => setIsFocused(true),
        blur: () => setIsFocused(false),
        afterInit: (editor: { editor: HTMLElement }) => {
          if (!ariaLabel) {
            return;
          }
          editor.editor.setAttribute('role', 'textbox');
          editor.editor.setAttribute('aria-multiline', 'true');
          editor.editor.setAttribute('aria-label', ariaLabel);
        },
      },
    };

    if (Object.keys(getJoditTranslations).length > 0) {
      baseConfig.i18n = {
        [joditLanguage]: getJoditTranslations,
      };
    }

    return baseConfig;
  }, [i18n.language, getJoditTranslations, disabled, theme, ariaLabel]);

  const handleChange = (newContent: string) => {
    // Only update the echo tracker — NOT editorValue — so Jodit's `value`
    // prop stays referentially stable while the user is typing.
    setLastEmittedValue(newContent);
    onChange?.(newContent);
  };

  const handleBlur = (newContent: string) => {
    setLastEmittedValue(newContent);
    onBlur?.(newContent);
  };

  return (
    <div
      data-slot="text-editor"
      data-focused={isFocused || undefined}
      data-invalid={ariaInvalid || undefined}
      data-disabled={disabled || undefined}
      className={cn('text-foreground flex flex-col gap-2', className)}
    >
      {label && (
        <InputLabel
          htmlFor={editorId}
          label={label}
          hint={hint}
          isRequired={isRequired}
          isInvalid={ariaInvalid}
        />
      )}
      <div
        data-slot="text-editor-frame"
        className={cn(
          'overflow-hidden rounded-md border-2 transition-colors',
          'border',
          isFocused && !ariaInvalid && 'border-primary',
          !isFocused && !ariaInvalid && 'border-input',
          ariaInvalid && 'border-destructive',
        )}
      >
        <JoditEditorWrapper
          key={theme}
          id={editorId}
          value={editorValue}
          config={config}
          onBlur={handleBlur}
          onChange={handleChange}
        />
      </div>
    </div>
  );
};

export { TextEditor };
export type { TextEditorProps };
